import { HttpClient, HttpParams } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { TokenService } from "../shared/token.service";
import { AuthenticationModel } from "../../models/authentication/authentication.model";
import { ModalService } from "../shared/modal.service";
import { AuthenticatedModel } from "../../models/authentication/authenticated.model";
import { UserService } from "../shared/user.service";
import { DnaService } from "../shared/dna.service";
import { MenuAdminService } from "../menuadmin/menuadmin.service";
import { Observable, Observer } from "rxjs";
import { AuthenticationResult } from '@app/models/authentication/authentication-result.model';
import swal from 'sweetalert';
import { SsoAuthenticationResult } from "@app/models/authentication/sso-authentication-result.model";
import { InteractionType } from "@azure/msal-browser";
import { MSAL_GUARD_CONFIG, MsalGuardConfiguration, MsalService } from "@azure/msal-angular";
import { Environment } from "@env/environment.global";

declare var $: any;

@Injectable({ providedIn: "root" })
export class AuthenticationService {
  private service = "AuthenticationService";
  public static authServiceLocalStorageKeyPrefix = "auth_";
  public authenticatedModel = new AuthenticatedModel();

  constructor(
    private http: HttpClient,
    private router: Router,
    private tokenService: TokenService,
    private modalService: ModalService,
    private userService: UserService,
    public menuService: MenuAdminService,
    private dnaService: DnaService,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService) {
  }

  private executeAuthenticationAction(preventHomeNavigation: boolean, token: string, observer: Observer<AuthenticatedModel>) {
    this.tokenService.set('token', token);
    this.userService.setUser.subscribe(() => {
      this.dnaService.getDna();
      this.dnaService.getDefaultDna();
      observer.next(this.userService.getLoggedInUser());
      if (!preventHomeNavigation) {
        this.router.navigate(['/']);
      }
    });
  }

  private executeErrorAction(error: any, observer: Observer<AuthenticatedModel>) {
    let message;
    if (error.status) {
      if (error.status === 404) {
        message = '404 - Authentication service is not accessible';
      } else if (error.status === 500) {
        message = '500 - Please try again later';
      } else {
        message = `${error.error}. Please try again later.`;
      }
    }

    this.modalService.showModal(message, 'Something went wrong');
    error.handled = true;
    observer.error(error);
  }

  private executeNeedsLogoutAction(preventHomeNavigation: boolean,
    authentication: AuthenticationModel | SsoAuthenticationResult,
    sso: boolean = false,
    observer: Observer<AuthenticatedModel>) {
    let method = sso ? "AuthenticateWithSsoResult" : "Authenticate"
    swal({
      title: 'Active session detected',
      text: 'You have logged in at another location. Your other sessions will be terminated!',
      icon: 'warning',
      buttons: {
        cancel: {
          text: 'No',
          visible: true,
          closeModal: true
        },
        confirm: {
          text: 'Yes',
          value: true,
          visible: true,
          className: 'bg-danger',
          closeModal: true
        }
      }
    }).then((isConfirm) => {
      if (isConfirm) {
        const params = new HttpParams()
          .set('forceLogin', 'true');
        this.http.post<AuthenticationResult>(`api/${this.service}/${method}`, authentication, { params })
          .subscribe(
            innerResult => {
              this.executeAuthenticationAction(preventHomeNavigation, innerResult.token, observer);
            },
            innerError => {
              this.executeErrorAction(innerError, observer);
            });
      }
    });
  }

  public authenticate(authentication: AuthenticationModel | SsoAuthenticationResult,
    preventHomeNavigation: boolean = false,
    sso: boolean = false): Observable<AuthenticatedModel> {
    let method = sso ? "AuthenticateWithSsoResult" : "Authenticate"

    return new Observable((observer) => {
      this.tokenService.clear();
      this.dnaService.clear();
      this.http.post<AuthenticationResult>(`api/${this.service}/${method}`, authentication)
        .subscribe(
          result => {
            if (!result.needsLogout) {
              this.executeAuthenticationAction(preventHomeNavigation, result.token, observer);
            } else {
              this.executeNeedsLogoutAction(preventHomeNavigation, authentication, sso, observer);
            }
          },
          error => {
            this.executeErrorAction(error, observer);
          }
        );
    });
  }

  public isAuthenticated(): boolean {
    return this.tokenService.exists('token');
  }

  public logout() {
    this.http.post(`api/${this.service}/logout`, {})
      .subscribe()
      .add(() => {
        this.clearSessionData();
        this.router.navigate(["/login"]);
      });
    if (this.loggedWithSso())
      this.logoutSso();
  }

  logoutSso() {
    const activeAccount =
      this.authService.instance.getActiveAccount() ||
      this.authService.instance.getAllAccounts()[0];

    if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
      this.authService.logoutPopup({
        account: activeAccount,
        authority: Environment.getSsoPolicy().authorities.logout.authority
      });
    } else {
      this.authService.logoutRedirect({
        account: activeAccount,
        authority: Environment.getSsoPolicy().authorities.logout.authority
      });
    }
  }

  public clearSessionData() {
    this.userService.logoutUser();
    this.tokenService.clear();
    this.dnaService.clear();
    this.menuService.clearMenu();
  }

  public isUserSessionValid() {
    const params = new HttpParams()
      .set('token', this.tokenService.get('token'));

    const options = { params: params, responseType: 'text' as 'text' };

    return this.http.get(`api/${this.service}/IsUserSessionValid`, options).subscribe(
      result => {
        if (result) {
          this.router.navigate(["/dashboard"]);
        } else {
          this.logout();
        }
      },
      error => console.log('couldnt run isusersessionvalid')
    );
  }


  public loggedWithSso(): boolean {
    return this.sso;
  }

  get sso(): boolean {
    return this.getItem("sso") == "true";
  }

  set sso(value: boolean) {
    this.saveItem("sso", value.toString());
  }

  private saveItem(key: string, value: string) {
    localStorage.setItem(AuthenticationService.authServiceLocalStorageKeyPrefix + key, value);
  }

  private getItem(key: string): string {
    return localStorage.getItem(AuthenticationService.authServiceLocalStorageKeyPrefix + key) ?? '';
  }
}
